/**
 * Settings + helpers for PDF Page Numbers tool.
 */

import {
  WATERMARK_COLOR_PRESETS,
  WATERMARK_CONTENT_TYPES,
  WATERMARK_POSITIONS,
  WATERMARK_TEXT_FONTS,
  WATERMARK_TILE_PRESETS,
  applyWatermarkPosition,
} from "../../../watermark/Popup/watermarkSettings";

export { WATERMARK_POSITIONS, WATERMARK_COLOR_PRESETS, WATERMARK_TEXT_FONTS };

export const PAGE_NUMBER_FORMATS = [
  { id: "n", label: "1", example: "1" },
  { id: "n_of_total", label: "1 / N", example: "1 / 12" },
  { id: "page_n", label: "Page 1", example: "Page 1" },
  { id: "page_n_of_total", label: "Page 1 of N", example: "Page 1 of 12" },
];

export const DEFAULT_PAGE_NUMBER_SETTINGS = {
  enabled: true,
  formatId: "n",
  /** Displayed number for page 1 (allows starting at 0, 5, etc.). */
  startAt: 1,
  fontId: "Arial",
  /** normal | bold */
  fontWeight: "normal",
  fontStyle: "normal",
  underline: false,
  strikethrough: false,
  letterSpacing: 0,
  lineHeight: 1.4,
  textAlign: "justify",
  textVerticalAlign: "middle",
  textScript: "normal",
  effect: "none",
  size: 1.2,
  opacity: 85,
  color: "#333333",
  position: "bottom",
  /** Edge inset as fraction of min(stageW, stageH), 0–0.4 */
  margin: 0.08,
  offsetX: 0,
  offsetY: 0,
  rotation: 0,
  skipFirstPage: false,
};

export function applyPageNumberPosition(settings, position) {
  return applyWatermarkPosition(settings, position);
}

/**
 * Absolute page index (1-based) → printed number given startAt.
 */
export function pageNumberValue(pageIndex1Based, startAt = 1) {
  const start = Math.floor(Number(startAt));
  const base = Number.isFinite(start) ? start : 1;
  return base + Math.max(0, Math.floor(pageIndex1Based) - 1);
}

/**
 * Format label for a given page.
 * @param {number} pageIndex1Based
 * @param {number} pageCount
 * @param {{ formatId?: string, startAt?: number }} settings
 */
export function formatPageNumberLabel(pageIndex1Based, pageCount, settings = {}) {
  const n = pageNumberValue(pageIndex1Based, settings.startAt);
  const lastPrinted = pageNumberValue(
    Math.max(1, Math.floor(Number(pageCount) || 1)),
    settings.startAt
  );

  switch (settings.formatId) {
    case "n_of_total":
      return `${n} / ${lastPrinted}`;
    case "page_n":
      return `Page ${n}`;
    case "page_n_of_total":
      return `Page ${n} of ${lastPrinted}`;
    case "n":
    default:
      return String(n);
  }
}

export function shouldShowPageNumber(pageIndex1Based, settings) {
  if (!settings?.enabled) return false;
  if (settings.skipFirstPage && pageIndex1Based === 1) return false;
  return true;
}

function pageNumberStyleFields(settings = {}) {
  return {
    fontId: settings.fontId || "Arial",
    fontWeight: settings.fontWeight || "normal",
    fontStyle: settings.fontStyle === "italic" ? "italic" : "normal",
    underline: Boolean(settings.underline),
    strikethrough: Boolean(settings.strikethrough),
    letterSpacing: Number(settings.letterSpacing) || 0,
    lineHeight: Number(settings.lineHeight) || 1.4,
    textAlign: settings.textAlign || "justify",
    textVerticalAlign: settings.textVerticalAlign || "middle",
    textScript: settings.textScript || "normal",
    effect: settings.effect || "none",
    size: settings.size ?? 1.2,
    opacity: settings.opacity ?? 85,
    color: settings.color || "#333333",
    position: settings.position || "bottom",
    margin: settings.margin ?? 0.08,
    offsetX: Number(settings.offsetX) || 0,
    offsetY: Number(settings.offsetY) || 0,
    rotation: Number(settings.rotation) || 0,
    /** Page numbers inset from edges; watermark maker stays flush. */
    flushEdges: false,
  };
}

/**
 * Map page-number settings → watermark settings for live WatermarkPreview / toolbar.
 */
export function pageNumberSettingsToWatermarkPreview(
  settings,
  pageIndex1Based,
  pageCount
) {
  const style = pageNumberStyleFields(settings);
  if (!shouldShowPageNumber(pageIndex1Based, settings)) {
    return {
      ...WATERMARK_TILE_PRESETS.single,
      contentType: WATERMARK_CONTENT_TYPES.TEXT,
      text: "",
      enabled: false,
      embed: false,
      ...style,
      logoUrl: null,
      logoNaturalWidth: 0,
      logoNaturalHeight: 0,
      presetId: null,
    };
  }

  return {
    ...WATERMARK_TILE_PRESETS.single,
    contentType: WATERMARK_CONTENT_TYPES.TEXT,
    text: formatPageNumberLabel(pageIndex1Based, pageCount, settings),
    enabled: true,
    embed: false,
    ...style,
    logoUrl: null,
    logoNaturalWidth: 0,
    logoNaturalHeight: 0,
    presetId: null,
  };
}

/**
 * Merge watermark-shaped toolbar / overlay / drag patches into page-number settings.
 * Only overwrites keys present on `wm` so partial drag updates (offset only)
 * do not wipe rotation, style, etc.
 */
export function watermarkPatchToPageNumberSettings(wm, prev = {}) {
  if (!wm || typeof wm !== "object") return prev;
  const next = { ...prev };

  if (wm.fontId !== undefined) next.fontId = wm.fontId;
  if (wm.fontWeight !== undefined) next.fontWeight = wm.fontWeight;
  if (wm.fontStyle !== undefined) {
    next.fontStyle = wm.fontStyle === "italic" ? "italic" : "normal";
  }
  if (wm.underline !== undefined) next.underline = Boolean(wm.underline);
  if (wm.strikethrough !== undefined) {
    next.strikethrough = Boolean(wm.strikethrough);
  }
  if (wm.letterSpacing !== undefined) {
    next.letterSpacing = Number(wm.letterSpacing) || 0;
  }
  if (wm.lineHeight !== undefined) {
    next.lineHeight = Number(wm.lineHeight) || prev.lineHeight || 1.4;
  }
  if (wm.textAlign !== undefined) {
    next.textAlign = wm.textAlign || prev.textAlign || "justify";
  }
  if (wm.textVerticalAlign !== undefined) {
    next.textVerticalAlign =
      wm.textVerticalAlign || prev.textVerticalAlign || "middle";
  }
  if (wm.textScript !== undefined) {
    next.textScript = wm.textScript || prev.textScript || "normal";
  }
  if (wm.effect !== undefined) next.effect = wm.effect || prev.effect || "none";
  if (wm.size !== undefined) next.size = wm.size ?? prev.size;
  if (wm.opacity !== undefined) next.opacity = wm.opacity ?? prev.opacity;
  if (wm.color !== undefined) next.color = wm.color ?? prev.color;
  if (wm.position !== undefined) {
    next.position = wm.position || prev.position;
  }
  if (wm.margin !== undefined && Number.isFinite(Number(wm.margin))) {
    next.margin = Math.max(0, Math.min(0.4, Number(wm.margin)));
  }
  if (wm.offsetX !== undefined) next.offsetX = Number(wm.offsetX) || 0;
  if (wm.offsetY !== undefined) next.offsetY = Number(wm.offsetY) || 0;
  if (wm.rotation !== undefined) {
    next.rotation = Math.max(
      -180,
      Math.min(180, Number(wm.rotation) || 0)
    );
  }

  return next;
}
